import { omit } from 'lodash'

import { AssetAccountTag } from '../../assets/utils/enums'

import { Tag, TagRef } from './types'

/** `Strategy · Private credit` — the one place a tag's display name is composed. */
export const tagLabel = ({ tag_name, tag_value }: TagRef) =>
  `${tag_name} · ${tag_value}`

/*
 * The two dimensions the app reserves for its own category tree, matched
 * case-insensitively because the name is whatever the repository stored.
 *
 * They are tags in the API's eyes and not in the user's: Category and
 * Subcategory drive the income tree and the tagging rules, are created and
 * renamed by those screens, and `TagFormName` will not let them be edited by
 * hand. Anywhere that offers "a tag" as free vocabulary means the other ones —
 * hence `isCommonTag`, which is the pair-level twin of `TagClass.isCommon` and
 * of the bunch store's `getCommonList`, for the many callers that hold a plain
 * pair rather than an entity.
 */
export const isCategoryDimension = (name?: string) =>
  name?.toLowerCase() === AssetAccountTag.CATEGORY.toLowerCase()

export const isSubcategoryDimension = (name?: string) =>
  name?.toLowerCase() === AssetAccountTag.SUBCATEGORY.toLowerCase()

/** A tag of the repository's own vocabulary — neither of the reserved two. */
export const isCommonTag = ({ tag_name }: TagRef) =>
  !isCategoryDimension(tag_name) && !isSubcategoryDimension(tag_name)

/** `isCommonTag` over a list, keeping whatever richer shape the entries carry. */
export const commonTags = <T extends TagRef>(tags: T[]): T[] =>
  tags.filter(isCommonTag)

/** Same dimension and same value. A tag's identity is its pair, not its id. */
export const isSameTag = (a: TagRef, b: TagRef) =>
  a.tag_name === b.tag_name && a.tag_value === b.tag_value

/**
 * The pair, carrying the repository's id when the repository has it.
 *
 * Derived, never trusted: whatever id came with the tag is dropped and looked
 * up again, because "this pair exists" is a fact about the catalogue and a
 * stale id would have whatever creates records link to a row that is not this
 * tag. An unmatched pair comes back without an id at all — that absence is what
 * says it has to be created.
 *
 * Only meaningful against a loaded catalogue. An empty one would mark every
 * applied tag as new, so callers gate on the store's `initFlag` rather than on
 * the list being non-empty — a repository with no tags yet is a real answer.
 */
export const withTagId = <T extends TagRef>(tag: T, catalogue: Tag[]): T => {
  const existing = catalogue.find(item => isSameTag(item, tag))

  return (existing?.id ? { ...tag, id: existing.id } : omit(tag, 'id')) as T
}

/** `withTagId` over a list — what a picker or a wizard actually holds. */
export const withTagIds = <T extends TagRef>(tags: T[], catalogue: Tag[]): T[] =>
  tags.map(tag => withTagId(tag, catalogue))
